import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Container, Form, Row } from 'react-bootstrap';
import Button from 'components/base/Button';
import api from 'app/api/axios-api';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import { getAccountDomain } from '@app/shared/utils/accountDomain.utils';

const CSAPlusCreateProject = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userId = localStorage.getItem('userId') || '';
  const accountDomain = getAccountDomain();

  const [projectTitle, setProjectTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    if (!projectTitle.trim()) {
      setError('Project title is required');
      return;
    }
    if (!accountDomain) {
      setError('Account domain is not set. Sign out and sign in again after completing registration with a domain.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post('/csa-plus/create-new', {
        projectTitle: projectTitle.trim(),
        description: description.trim() || undefined,
        domainName: accountDomain,
        userId: userId || undefined
      });

      const project = response.data?.project ?? response.data;
      const projectId = project?.id ?? project?._id ?? project?.projectId;
      if (projectId) {
        localStorage.setItem('projectId', projectId);
        dispatch(
          setToast({
            show: true,
            title: 'Project created',
            message: 'Your project has been created successfully.'
          })
        );
        navigate(`/csa+/project-details/${encodeURIComponent(projectId)}`);
      } else {
        dispatch(
          setToast({
            show: true,
            title: 'Project created',
            message: 'Your project has been created successfully.'
          })
        );
        navigate('/csa+/all-projects');
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? err?.message ?? 'Failed to create project';
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <h2 className="mb-4">Create New Project</h2>
      <p className="text-body-secondary mb-4">Create a new CSA+ project</p>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={8} lg={6}>
            <Form.Group className="mb-3">
              <Form.Label>Project Title *</Form.Label>
              <Form.Control
                type="text"
                value={projectTitle}
                onChange={e => setProjectTitle(e.target.value)}
                placeholder="Enter project title"
                required
                disabled={isSubmitting}
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Enter description (optional)"
                disabled={isSubmitting}
              />
            </Form.Group>
            {error && (
              <div className="alert alert-danger mb-3" role="alert">
                {error}
              </div>
            )}
            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Project'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Container>
  );
};

export default CSAPlusCreateProject;
